import * as React from 'react';
import { useDispatch } from 'react-redux';
import { closeWorkspaceOverlay } from 'common/store/workspaces/actions';
import { ContentTypes } from 'common/store/common/constants';
import { ThresholdLevel, ThresholdScoringMessageTypes } from '../types';
import { updateContentInContextToLiveRevision } from '../../contentEditor/actions';
import apiService from '../../../services/apiService';
import storeManager from '../../../store';
import { RootState } from '../../../store/rootReducer';
import ThresholdScoringEditor from './ThresholdScoringEditor';
import { editorContentSaveInProgress } from '../../contentEditor/actions';

const CONTENT_EDITOR_FEATURE_IDS = [
    'feature_contentEditor_author_edit_2_0',
    'feature_contentEditor_author_question_edit_only_with_editor_2_0',
];

interface ThresholdScoringOverlayProps {
    contentItemId?: number;
    thresholds?: ThresholdLevel[];
    maxScore?: number;
    minScore?: number;
    step?: number;
    nrOfDecimals?: number;
    options?: {
        contentItemId?: number;
        thresholds?: ThresholdLevel[];
        maxScore?: number;
        minScore?: number;
        step?: number;
        nrOfDecimals?: number;
    };
}

const ThresholdScoringOverlay: React.FC<ThresholdScoringOverlayProps> = (props) => {
    const dispatch = useDispatch();

    const contentItemId = props?.contentItemId ?? props?.options?.contentItemId;
    const thresholds = props?.thresholds ?? props?.options?.thresholds ?? [];
    const maxScore = props?.maxScore ?? props?.options?.maxScore ?? 0;
    const minScore = props?.minScore ?? props?.options?.minScore ?? 0;
    const step = props?.step ?? props?.options?.step ?? 1;
    const nrOfDecimals = props?.nrOfDecimals ?? props?.options?.nrOfDecimals ?? 0;

    // Per spec AC2.5/AC2.6: step is always 1 (integer increment), display as whole numbers
    const effectiveStep = Math.max(step, 1);
    const effectiveDecimals = effectiveStep >= 1 ? 0 : nrOfDecimals;

    const getContentEditorIframe = (): HTMLIFrameElement | null => {
        const state = storeManager.getState() as RootState & Record<string, any>;
        const featureId = CONTENT_EDITOR_FEATURE_IDS.find((id) => !!state[id]?.iframeTarget)
            || CONTENT_EDITOR_FEATURE_IDS.find((id) => !!state[id]);

        if (featureId && state[featureId]?.iframeTarget) {
            return state[featureId].iframeTarget;
        }

        return null;
    };

    const handleSave = (updatedThresholds: ThresholdLevel[]): Promise<void> => {
        const iframe = getContentEditorIframe();
        if (iframe?.contentWindow) {
            iframe.contentWindow.postMessage(
                {
                    type: ThresholdScoringMessageTypes.SAVE_THRESHOLD_SCORING,
                    payload: {
                        contentItemId,
                        thresholds: updatedThresholds,
                    },
                },
                window.location.origin,
            );
        }

        // Wait for the iframe to finish saving the new revision before updating context
        return new Promise<void>((resolve) => {
            setTimeout(async () => {
                const state = storeManager.getState() as RootState;
                const featureId = CONTENT_EDITOR_FEATURE_IDS.find((id) => !!(state as any)[id]?.iframeTarget)
                    || CONTENT_EDITOR_FEATURE_IDS.find((id) => !!(state as any)[id]);

                if (!featureId || !contentItemId) {
                    resolve();
                    return;
                }

                try {
                    const { context } = state;
                    const { contentType } = context.currentItemContext;
                    let allItems: any[] = [];

                    storeManager.dispatch(editorContentSaveInProgress(featureId, true));

                    if (contentType === ContentTypes.QUESTION_SET) {
                        const freshAssessment = await apiService.getAssessmentData(context.contextId.toString());
                        allItems = (freshAssessment.sections || []).reduce(
                            (acc: any[], section: any) => acc.concat(section.items || []), [],
                        );
                    } else if (contentType === ContentTypes.SECTION) {
                        const freshSection = await apiService.fetchGetSection(context.contextId, { context: 'author' });
                        allItems = freshSection?.items || [];
                    }

                    const matchingItem = allItems.find((item: any) => item.id === contentItemId);

                    if (matchingItem && matchingItem.resource?.liveRevisionId !== matchingItem.resource?.revisionId) {
                        storeManager.dispatch(updateContentInContextToLiveRevision(featureId, contentItemId));
                    }
                } catch (err) {
                    console.error('Threshold scoring: sync content after save failed', err);
                }
                finally {
                    storeManager.dispatch(editorContentSaveInProgress(featureId, false));
                    resolve();
                }
            }, 2000);
        });
    };

    const handleClose = () => {
        const iframe = getContentEditorIframe();
        if (iframe?.contentWindow) {
            iframe.contentWindow.postMessage(
                {
                    type: ThresholdScoringMessageTypes.CLOSE_THRESHOLD_SCORING_OVERLAY,
                    payload: { contentItemId },
                },
                window.location.origin,
            );
        }
        dispatch(closeWorkspaceOverlay());
    };

    if (!contentItemId) {
        return null;
    }

    return (
        <ThresholdScoringEditor
            thresholds={thresholds}
            maxScore={maxScore}
            minScore={minScore}
            step={effectiveStep}
            nrOfDecimals={effectiveDecimals}
            contentItemId={contentItemId}
            onSave={handleSave}
            onClose={handleClose}
        />
    );
};

export default ThresholdScoringOverlay;
